import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  ContentRow,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import { get } from '../utils/apiUtils';
import { fetchEntityByPagination, fetchEntityCount, _getAllEntitiesByFilter } from "../services/entityService_V2";
import { faWindowRestore } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../components/common/Pagination';



const EntityList_V2 = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [entityList, setEntityList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [debounceSearchQuery, setDebounceSearchQuery] = useState(searchTerm);
  
  
  const itemsPerPage = 10;


  // Mock data for knowledge base documents
  const documents = [
    {
      id: 'DOC-001',
      title: 'Claims Processing Guidelines',
      type: 'Policy Document',
      category: 'Claims',
      lastUpdated: '2025-02-15',
      status: 'active',
      size: '1.2 MB',
      pages: 24,
      accessLevel: 'All Agents'
    },
    {
      id: 'DOC-002',
      title: 'Underwriting Manual',
      type: 'Reference Guide',
      category: 'Underwriting',
      lastUpdated: '2025-01-28',
      status: 'active',
      size: '3.5 MB',
      pages: 78,
      accessLevel: 'Underwriting Agents'
    },
    {
      id: 'DOC-003',
      title: 'Fraud Detection Patterns',
      type: 'Technical Document',
      category: 'Fraud',
      lastUpdated: '2025-03-10',
      status: 'active',
      size: '0.8 MB',
      pages: 15,
      accessLevel: 'Fraud Agents'
    },
    {
      id: 'DOC-004',
      title: 'Customer Service Scripts',
      type: 'Training Material',
      category: 'Customer Service',
      lastUpdated: '2025-02-22',
      status: 'active',
      size: '1.0 MB',
      pages: 32,
      accessLevel: 'Customer Service Agents'
    },
    {
      id: 'DOC-005',
      title: 'Medical Coding Reference',
      type: 'Reference Guide',
      category: 'Claims',
      lastUpdated: '2025-03-05',
      status: 'active',
      size: '4.2 MB',
      pages: 112,
      accessLevel: 'Claims Agents'
    },
    {
      id: 'DOC-006',
      title: 'Policy Terms and Conditions',
      type: 'Legal Document',
      category: 'Legal',
      lastUpdated: '2025-01-15',
      status: 'active',
      size: '2.8 MB',
      pages: 64,
      accessLevel: 'All Agents'
    },
  ];

  useEffect(() => {
    const fetchEntityData = async () => {
      try {
        let filter = {};
        if(debounceSearchQuery) {
          filter = { 'name' :{ 'ilike' : `${debounceSearchQuery}`} };
        }
        const sort = {};
        const query = {limit: itemsPerPage, skip: (currentPage - 1) * itemsPerPage}
        const response = await _getAllEntitiesByFilter(filter, sort, query )
        setEntityList(response || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
        navigate('/login'); // Redirect to login if unauthorized
      }
    };

    fetchEntityData();
  }, [navigate, setEntityList, currentPage, debounceSearchQuery]);

  
  useEffect(() => {
    const fetchCountOfEntity = async () => {
      try {
        let filter = {};
        if (debounceSearchQuery) {
          filter = { ...filter, 'name': { 'ilike': `${debounceSearchQuery}` } }
        }
        const responce = await fetchEntityCount(filter);
        setTotalItems(responce)
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    fetchCountOfEntity();
  }, [debounceSearchQuery])

  useEffect(() => {
    const timer = setTimeout(() => { setDebounceSearchQuery(searchTerm) }, 500);
    return () => {
      clearTimeout(timer)
    }
  }, [searchTerm])


  // Filter documents based on search term
  const filteredEntities = (entityList || []).filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entity.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'active';
      case 'INACTIVE': return 'active';
      case 'archived': return 'default';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };
  // Utility function to format date
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB');
  };
  // Handle view claim details
  const handleViewEntity = (entityId) => {
    navigate(`/entity-details/${entityId}`);
  };
  const handleListEntity = (entityId, doc) => {
    navigate(`/entity-lists/${entityId}`, {
      state: {
        name: doc.name,
      },
    });
  };




  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faWindowRestore}
          title="Entity"
          description="The Entity page is designed to manage the knowledge sources and documents that power AI agents."
        >
          <div style={{ display: 'flex', gap: "1rem" }}>
            <SearchInput
              type="text"
              placeholder="Search Entities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button primary onClick={() => handleViewEntity('new')}>Add Entity</Button></div>
        </PageHeaderWithInfo>



        <Card>
          <Table>
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Version</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Last Updated</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {(entityList && entityList.length > 0) && (filteredEntities.map(doc => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>{doc.version}</TableCell>
                  <TableCell>{doc.type === 'entity' && ('Entity')} {doc.type === 'sub_entity' && ('Sub Entity')} {doc.type === 'document' && ('Document')}</TableCell>
                  <TableCell>{formatDate(doc.updated_at)}</TableCell>
                  <TableCell>
                    <StatusBadge status={getStatusColor(doc.status)}>
                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => handleViewEntity(doc.id)}>Edit</Button>
                      <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} onClick={() => handleListEntity(doc.name, doc)}>List</Button>
                    </div>
                  </TableCell>
                </TableRow>
              )))}
            </tbody>
          </Table>
        </Card>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalItems}
          setCurrentPage={setCurrentPage}
          setTotalPages={setTotalPages}
      />
      </motion.div>
    </PageContainer>
  );
};

export default EntityList_V2;